import { wormholeExecutorQuoteUrl } from "@/constants/flags.constant";
import type {
  WormholeExecutorQuoteRequest,
  WormholeExecutorQuoteResponse,
  WormholeExecutorQuoteResult,
} from "@/features/bridge/types/wormhole-executor.types";

const QUOTE_PATH = "/v0/quote";

const toQuoteResult = (
  payload: WormholeExecutorQuoteResponse,
): WormholeExecutorQuoteResult => ({
  signedQuote: payload.signedQuote,
  estimatedCost: BigInt(payload.estimatedCost),
});

export const getExecutorSignedQuote = async (
  params: WormholeExecutorQuoteRequest,
): Promise<WormholeExecutorQuoteResult> => {
  const quoteUrl = `${wormholeExecutorQuoteUrl}${QUOTE_PATH}`;
  const routeLabel = `${params.srcChain} -> ${params.dstChain}`;

  const response = await fetch(quoteUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    let errorReason = `${response.status} ${response.statusText}`.trim();
    try {
      const errorBody = await response.json();
      const message =
        errorBody?.message || errorBody?.error || JSON.stringify(errorBody);
      if (message) errorReason = message;
    } catch {
      // Ignore parsing errors and use status-based fallback.
    }
    throw new Error(
      `Executor quote request failed for route ${routeLabel} via ${quoteUrl}: ${errorReason}`,
    );
  }

  const quotePayload =
    (await response.json()) as Partial<WormholeExecutorQuoteResponse>;

  if (!quotePayload?.signedQuote || !quotePayload?.estimatedCost) {
    throw new Error(
      `Executor quote response is missing required fields for route ${routeLabel} via ${quoteUrl}`,
    );
  }

  return toQuoteResult(quotePayload as WormholeExecutorQuoteResponse);
};
